"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserBehaviorTrackService = void 0;
var CommonService_decorator_1 = require("../../core/decorators/CommonService.decorator");
/**
 * @summary Check newrelic is available or not.
 */
function isNewrelicAvailable() {
    if (window.newrelic && window.newrelic.interaction) {
        return true;
    }
    return false;
}
function setCustomAttribute(key, value) {
    if (isNewrelicAvailable()) {
        window.newrelic.setCustomAttribute(key, value);
    }
}
function setCustomAttributes(obj) {
    if (isNewrelicAvailable() && typeof obj === 'object' && obj) {
        Object.keys(obj).forEach(function (key) {
            window.newrelic.setCustomAttribute(key, obj[key]);
        });
    }
}
var noop = function () {
    // noop
};
/**
 * @summary Wrapped the API in newrelic interaction level.
 */
var Interaction = /** @class */ (function () {
    function Interaction() {
    }
    Interaction.prototype.createTracer = function (name, callback) {
        if (isNewrelicAvailable()) {
            return window.newrelic.interaction().createTracer(name, callback);
        }
        return this && noop;
    };
    Interaction.prototype.setAttribute = function (key, value) {
        if (isNewrelicAvailable()) {
            window.newrelic.interaction().setAttribute(key, value);
        }
        return this;
    };
    Interaction.prototype.setAttributes = function (obj) {
        if (isNewrelicAvailable() && typeof obj === 'object' && obj) {
            Object.keys(obj).forEach(function (key) {
                window.newrelic.interaction().setAttribute(key, obj[key]);
            });
        }
        return this;
    };
    Interaction.prototype.setName = function (name, trigger) {
        if (isNewrelicAvailable()) {
            window.newrelic.interaction().setName(name, trigger);
        }
        return this;
    };
    Interaction.prototype.onEnd = function (callback) {
        if (isNewrelicAvailable() && typeof callback === 'function') {
            window.newrelic.interaction().onEnd(callback);
        }
        return this;
    };
    Interaction.prototype.save = function () {
        if (isNewrelicAvailable()) {
            window.newrelic.interaction().save();
        }
        return this;
    };
    return Interaction;
}());
function interaction() {
    return new Interaction();
}
/**
 * This is the main public entry point.
 * ```
 * //usage
 * import {instance, UserBehaviorTrackService} from 'seismic-common'
 * const userBehaviorTrackService = instance.getService(UserBehaviorTrackService);
 *
 * //tracer the init process.
 * var initTracerCallback = userBehaviorTrackService.createTracer('init', () => {});
 * initTracerCallback();
 *
 * //set common basic attributes
 * userBehaviorTrackService.setCustomAttribute('userId', 'xxxx');
 * userBehaviorTrackService.setCustomAttributes({userId: 'xxxx', userName: 'xxxx'});
 *
 * //set attributes in current interaction.
 * userBehaviorTrackService.setAttribute('contentId', 'xxxx');
 * userBehaviorTrackService.setAttributes({contentId: 'xxxx', contentName: 'xxxx'});
 *
 * //set the name of current interaction.
 * userBehaviorTrackService.setName('init load');
 * userBehaviorTrackService.setName('init load', 'user click');
 *
 * //do something when interaction on end.
 * userBehaviorTrackService.onEnd(function(){//do some thing});
 * ```
 * @namespace userBehaviourTrack
 */
// tslint:disable-next-line max-classes-per-file
var UserBehaviorTrackService = /** @class */ (function () {
    function UserBehaviorTrackService() {
        /**
         * @summary Times sub-components of a SPA interaction separately, including wait time and JS execution time.
         * @param {string} name - This will be used as the name of the tracer. Required.
         * @param {function} callback - A callback that contains the synchronous work to run at the end of the async work. Optional.
         */
        this.createTracer = function (name, callback) {
            return interaction().createTracer(name, callback);
        };
        /**
         * @summary Adds a custom SPA attribute only to the current interaction in New Relic Browser.
         * @param {string} key - Used as the attribute name on the BrowserInteraction event. Required.
         * @param {any} value - Used as the attribute value on the BrowserInteraction event. Required.
         */
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        this.setAttribute = function (key, value) { return interaction().setAttribute(key, value); };
        /**
         * @summary Adds multiple custom SPA attributes only to the current interaction in New Relic Browser, same as setAttribute.
         * @param {object} obj - Used as the custom attributes on the BrowserInteraction event. Required.
         */
        this.setAttributes = function (obj) { return interaction().setAttributes(obj); };
        /**
         * @summary Sets the name and trigger of a SPA's browser interaction that is not a route change or URL change.
         * @param {string} name - This will set the browserInteractionName attribute in the BrowserInteraction event. Required.
         * @param {string} trigger - This will set the TRIGGER attribute on the BrowserInteraction event. Optional.
         */
        this.setName = function (name, trigger) { return interaction().setName(name, trigger); };
        /**
         * @summary Adds custom attributes for SPA interactions to the end of an event.
         * @param {function} callback - A function that accepts the interaction context object as its only argument. Required.
         */
        this.onEnd = function (callback) { return interaction().onEnd(callback); };
        /**
         * @summary Ensures a SPA browser interaction will be saved when it ends.
         */
        this.save = function () { return interaction().save(); };
        /**
         * @summary Adds a user-defined attribute name and value to subsequent events on the page. Once an attribute is set, New Relic Insights records it with all PageAction events until the page is reloaded or the attribute is manually unset.
         * @param {string} key - Used as the attribute name on the BrowserInteraction event and the PageAction event. Required.
         * @param {any} value - Used as the attribute value on the BrowserInteraction event and the PageAction event. Required.
         */
        this.setCustomAttribute = function (key, value) { return setCustomAttribute(key, value); };
        /**
         * @summary Adds multiple custom attributes to subsequent events on the page, same as setCustomAttribute.
         * @param {object} obj - Used as the custom attributes on the BrowserInteraction event and the PageAction event. Required.
         */
        this.setCustomAttributes = function (obj) { return setCustomAttributes(obj); };
    }
    UserBehaviorTrackService = __decorate([
        CommonService_decorator_1.CommonService('UserBehaviorTrackService')
    ], UserBehaviorTrackService);
    return UserBehaviorTrackService;
}());
exports.UserBehaviorTrackService = UserBehaviorTrackService;
//# sourceMappingURL=UserBehaviorTrack.service.js.map